import { useState, useCallback } from 'react';
import apiClient from '../services/api';

/**
 * Hook compartido por Armar Pedido y la pantalla de Minutas para
 * implementar el acordeón "Buscar minutas anteriores".
 *
 * Maneja el estado del toggle, las fechas del rango, el flag de carga
 * y los resultados. La función `buscar` llama al endpoint
 * `/api/minutas-nuevas` con `fechaInicio`/`fechaFin` (más el `estado`
 * opcional pasado al hook) y opcionalmente filtra IDs ya presentes en
 * otra lista (las vigentes) para no duplicarlos en la sección
 * histórica.
 *
 * `opciones.estado` controla el filtro por estado de la minuta:
 *   - undefined → trae todos los estados (caso pantalla Minutas:
 *     el equipo debe ver borradores, completadas y canceladas
 *     históricas).
 *   - 'activa'  → sólo minutas activas (caso Armar Pedido:
 *     los clientes no piden sobre borradores ni canceladas).
 */
export interface UseMinutasHistoricasOpciones {
  estado?: string;
}

export interface UseMinutasHistoricasResult {
  mostrar: boolean;
  setMostrar: (v: boolean | ((prev: boolean) => boolean)) => void;
  fechaDesde: string;
  setFechaDesde: (v: string) => void;
  fechaHasta: string;
  setFechaHasta: (v: string) => void;
  cargando: boolean;
  resultados: any[];
  buscado: boolean;
  buscar: (idsExcluir?: Set<number>) => Promise<void>;
  errorMensaje: string | null;
}

export function useMinutasHistoricas(
  opciones: UseMinutasHistoricasOpciones = {}
): UseMinutasHistoricasResult {
  const { estado } = opciones;
  const [mostrar, setMostrar] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [cargando, setCargando] = useState(false);
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscado, setBuscado] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState<string | null>(null);

  const buscar = useCallback(
    async (idsExcluir?: Set<number>) => {
      try {
        setCargando(true);
        setErrorMensaje(null);
        const params: any = { limit: 50 };
        if (estado) params.estado = estado;
        if (fechaDesde) params.fechaInicio = fechaDesde;
        if (fechaHasta) params.fechaFin = fechaHasta;

        const response = await apiClient.get('/minutas-nuevas', { params });

        if (response.data?.success) {
          const data = response.data.data || [];
          const filtrados = idsExcluir
            ? data.filter((m: any) => !idsExcluir.has(m.id))
            : data;
          setResultados(filtrados);
          setBuscado(true);
        }
      } catch (error: any) {
        console.error('Error buscando minutas históricas:', error);
        setErrorMensaje('Error al buscar minutas históricas');
      } finally {
        setCargando(false);
      }
    },
    [fechaDesde, fechaHasta, estado]
  );

  return {
    mostrar,
    setMostrar,
    fechaDesde,
    setFechaDesde,
    fechaHasta,
    setFechaHasta,
    cargando,
    resultados,
    buscado,
    buscar,
    errorMensaje,
  };
}
